import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Globe, CheckCircle, XCircle, Loader2, Clock, Download, Trash2, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const API = '/api';

function StatusBadge({ status }) {
  const map = {
    pending: { cls: 'bg-zinc-100 text-zinc-600 border-zinc-200', icon: Clock, label: 'Pending' },
    processing: { cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Loader2, label: 'Checking...' },
    completed: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Done' },
    failed: { cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'Failed' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs font-medium border ${s.cls}`}>
      <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
      {s.label}
    </Badge>
  );
}

export default function DomainCheckerPage() {
  const [domainsText, setDomainsText] = useState('');
  const [checks, setChecks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchChecks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/domain-checks`, { withCredentials: true });
      setChecks(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!selectedCheckId) { setDetail(null); return; }
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`${API}/domain-check/${selectedCheckId}`, { withCredentials: true });
        if (!cancelled) setDetail(data);
      } catch (err) { console.error(err); }
    };
    fetchDetail();
    const interval = setInterval(fetchDetail, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedCheckId]);

  useEffect(() => {
    fetchChecks();
    const iv = setInterval(fetchChecks, 10000);
    return () => clearInterval(iv);
  }, [fetchChecks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const domains = domainsText.split('\n').map(d => d.trim()).filter(Boolean);
    if (domains.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/domain-check`, { domains }, { withCredentials: true });
      setDomainsText('');
      toast.success(`Checking ${domains.length} domain(s)`);
      setSelectedCheckId(data.id);
      fetchChecks();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start check');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/domain-check/${id}`, { withCredentials: true });
      if (selectedCheckId === id) setSelectedCheckId(null);
      toast.success('Deleted');
      fetchChecks();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = (check) => {
    if (!check?.available_domains?.length) return;
    const text = check.available_domains.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'available_domains.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="domain-checker-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column — Input + History */}
          <div className="lg:col-span-1 space-y-5">
            {/* Input */}
            <div className="border border-zinc-200 bg-white p-5" data-testid="domain-input-section">
              <h2 className="text-lg font-bold tracking-tight font-heading text-zinc-950 mb-1">
                <Globe className="w-5 h-5 inline mr-2 -mt-0.5" />Domain Checker
              </h2>
              <p className="text-sm text-zinc-500 font-body mb-3">Check domain availability (one per line)</p>
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="domain-check-form">
                <textarea
                  value={domainsText}
                  onChange={(e) => setDomainsText(e.target.value)}
                  placeholder={"example.com\nmydomain.net\ncoolsite.io"}
                  className="w-full h-36 font-mono text-sm border border-zinc-300 rounded-sm p-3 focus:ring-2 focus:ring-blue-600 focus:outline-none resize-y bg-white"
                  data-testid="domain-input"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-400">{domainsText.split('\n').filter(d => d.trim()).length} domain(s)</span>
                  <Button type="submit" disabled={submitting || !domainsText.trim()} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-5 h-9" data-testid="check-domains-button">
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</> : <><Search className="w-4 h-4 mr-2" /> Check</>}
                  </Button>
                </div>
              </form>
            </div>

            {/* History */}
            <div className="border border-zinc-200 bg-white" data-testid="domain-checks-history">
              <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">History</span>
              </div>
              <ScrollArea className="max-h-[400px]">
                {checks.length === 0 ? (
                  <p className="p-4 text-xs text-zinc-400 font-body">No checks yet</p>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {checks.map(c => (
                      <div
                        key={c.id}
                        onClick={() => setSelectedCheckId(c.id)}
                        role="button"
                        tabIndex={0}
                        className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors cursor-pointer ${selectedCheckId === c.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''}`}
                        data-testid={`check-item-${c.id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <StatusBadge status={c.status} />
                          <div className="flex gap-1">
                            {c.status === 'completed' && c.available_count > 0 && (
                              <span onClick={(e) => { e.stopPropagation(); setSelectedCheckId(c.id); }} role="button" tabIndex={0} className="text-zinc-400 hover:text-blue-600 cursor-pointer" data-testid={`download-check-${c.id}`}><Download className="w-3.5 h-3.5" /></span>
                            )}
                            <span onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }} role="button" tabIndex={0} className="text-zinc-400 hover:text-rose-600 cursor-pointer" data-testid={`delete-check-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></span>
                          </div>
                        </div>
                        <div className="text-xs text-zinc-600">
                          {c.total_count || 0} domains {c.status === 'completed' && <span className="text-emerald-600 font-medium ml-1">{c.available_count || 0} available</span>}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* Right column — Results */}
          <div className="lg:col-span-2 border border-zinc-200 bg-white" data-testid="domain-results">
            <div className="px-4 py-2.5 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Results</span>
              {detail?.status === 'completed' && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">
                    <span className="text-emerald-600 font-bold">{detail.available_count}</span> available / <span className="font-bold">{detail.total_count}</span> total
                  </span>
                  {detail.available_domains?.length > 0 && (
                    <button onClick={() => handleDownload(detail)} className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 border border-emerald-200" data-testid="download-available-btn">
                      <Download className="w-3 h-3" /> Download Available
                    </button>
                  )}
                </div>
              )}
            </div>

            {!detail ? (
              <div className="flex flex-col items-center justify-center h-64 text-zinc-400">
                <Globe className="w-10 h-10 mb-3 opacity-40" />
                <p className="text-sm font-body">Enter domains and click Check to see results</p>
              </div>
            ) : detail.status === 'processing' ? (
              <div className="p-8 space-y-4">
                <div className="flex items-center gap-3">
                  <Progress value={detail.progress} className="h-2 flex-1" />
                  <span className="text-sm font-mono text-zinc-500">{detail.progress}%</span>
                </div>
                <p className="text-sm text-zinc-500 font-body text-center">Checking domains...</p>
              </div>
            ) : detail.status === 'failed' ? (
              <div className="p-6 text-sm text-rose-600">{detail.error || 'Check failed'}</div>
            ) : detail.results ? (
              <ScrollArea className="h-[calc(100vh-280px)]">
                <table className="w-full text-sm" data-testid="domain-results-table">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200 text-left sticky top-0">
                      <th className="px-4 py-2 text-xs font-medium text-zinc-600 uppercase tracking-wider">Domain</th>
                      <th className="px-4 py-2 text-xs font-medium text-zinc-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2 text-xs font-medium text-zinc-600 uppercase tracking-wider">Registrar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...detail.results].sort((a, b) => {
                      const aVal = a.available === true ? 2 : a.error ? 0 : 1;
                      const bVal = b.available === true ? 2 : b.error ? 0 : 1;
                      return bVal - aVal;
                    }).map((r, i) => (
                      <tr key={r.domain || i} className={`border-b border-zinc-100 ${i % 2 === 1 ? 'bg-zinc-50/50' : ''}`} data-testid={`domain-result-${r.domain}`}>
                        <td className="px-4 py-2 font-mono text-xs text-zinc-700">{r.domain}</td>
                        <td className="px-4 py-2">
                          {r.available === true ? (
                            <Badge variant="outline" className="text-xs border-emerald-200 bg-emerald-50 text-emerald-700 gap-1">
                              <CheckCircle className="w-3 h-3" /> Available
                            </Badge>
                          ) : r.available === false ? (
                            <Badge variant="outline" className="text-xs border-zinc-200 bg-zinc-50 text-zinc-600 gap-1">
                              <XCircle className="w-3 h-3" /> Registered
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs border-amber-200 bg-amber-50 text-amber-700 gap-1">
                              <Clock className="w-3 h-3" /> Unknown
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-500">{r.error || r.registrar || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
